import { ImageFilmstrip } from "@/components/coverage/ImageFilmstrip";
import { useCoverageBetaStore } from "@/store/useCoverageBetaStore";
import { resolveGalleryImageUrl } from "@/utils/helpers";
import { useImageGalleryQuery } from "@labeling-tools/api/lebron/react-query-hooks";
import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

/**
 * Grouped query images: a strip of the task's views under the query image.
 * Picking one shows it in the query pane, and new boxes are drawn onto it.
 * Renders nothing for an ungrouped task.
 */
export const QueryViewFilmstrip = () => {
  const groupImages = useCoverageBetaStore((s) => s.groupImages);
  const primaryImageId = useCoverageBetaStore((s) => s.primaryImageId);
  const activeImageId = useCoverageBetaStore((s) => s.activeImageId);
  const setActiveImageId = useCoverageBetaStore((s) => s.setActiveImageId);
  const recordInteraction = useCoverageBetaStore((s) => s.recordInteraction);
  const areaImageIds = useCoverageBetaStore(
    useShallow((s) =>
      Object.values(s.idxToFeEmptyArea)
        .filter((a) => !a.isDeleted)
        .map((a) => a.imageId ?? s.primaryImageId),
    ),
  );

  const imageIds = useMemo(
    () => groupImages.map((image) => image.imageId),
    [groupImages],
  );
  const { data: gallery } = useImageGalleryQuery(
    imageIds.length > 1 ? imageIds : undefined,
  );
  const imageUrls = useMemo(() => {
    const urls = new Map<string, string>();
    for (const image of gallery?.images ?? []) {
      const url = resolveGalleryImageUrl(image);
      if (image.id && url) urls.set(image.id, url);
    }
    return urls;
  }, [gallery]);

  const boxCounts = useMemo(() => {
    const counts = new Map<string, number>();
    areaImageIds.forEach((id) => counts.set(id, (counts.get(id) ?? 0) + 1));
    return counts;
  }, [areaImageIds]);

  return (
    <ImageFilmstrip
      images={groupImages}
      imageUrls={imageUrls}
      activeImageId={activeImageId ?? primaryImageId}
      onSelect={(imageId) => {
        setActiveImageId(imageId);
        recordInteraction("view_nav", "click");
      }}
      boxCounts={boxCounts}
    />
  );
};
